import type { IAuthContext } from "@/application/ports/IAuthContext";
import type { User } from "@/domain";
import type { AuthState } from "@/domain/types";
import { getErrorMessage } from "@/domain/errors";
import { useToastStore } from "@/stores/useToastStore";
import { identifyUser, resetAnalytics } from "@/infrastructure/analytics";
import {
  getLoginUseCase,
  getLoginWithTokensUseCase,
  getLogoutUseCase,
  getTokenManager,
  getUserStorageService,
  getValidateAuthUseCase,
  getSessionExpiredNotifier,
  getLoginRedirectNotifier,
} from "@/infrastructure/di";
import { queryKeys, useRefreshEntities, useSessionCache } from "@/application/queries";
import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from "react";

export const AuthContext = createContext<IAuthContext | undefined>(undefined);

export function useAuth(): IAuthContext {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

const NO_ORGANIZATION = "";

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const session = useSessionCache();
  const refresh = useRefreshEntities(NO_ORGANIZATION);
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const showToast = useToastStore((state) => state.showToast);

  const refreshOrganizations = useCallback(
    () => refresh([], { keys: [queryKeys.organization.userOrganizations()] }),
    [refresh],
  );

  const checkAuthStatus = useCallback(async () => {
    setAuthState((prev) => (prev.isAuthenticated ? prev : { ...prev, isLoading: true }));

    try {
      const result = await getValidateAuthUseCase().execute();
      setAuthState(result);

      if (result.isAuthenticated && result.user) {
        identifyUser(result.user);
        refreshOrganizations();
      }
    } catch (error) {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: getErrorMessage(error, "Authentication check failed"),
      });
    }
  }, [refreshOrganizations]);

  const login = useCallback(
    async (email: string, password: string) => {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const result = await getLoginUseCase().execute({ email, password });

        if (!result.success) {
          setAuthState((prev) => ({
            ...prev,
            isLoading: false,
            error: result.error || "Login failed",
          }));
          /**
           * Preserve the underlying error (e.g. ApiClientError) via Error.cause
           * so the presentation layer can detect network / 5xx and surface a
           * specific message instead of the generic "login failed" toast.
           */
          throw new Error(result.error || "Login failed", {
            cause: result.cause,
          });
        }

        setAuthState({
          user: result.user!,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });

        identifyUser(result.user!);

        // Reset session expiry guard so future 401s are handled correctly
        const { resetSdkSessionState } = await import("@/infrastructure/api/sdkConfig");
        resetSdkSessionState();

        refreshOrganizations();
      } catch (error) {
        setAuthState((prev) => ({
          ...prev,
          isLoading: false,
          error: getErrorMessage(error, "Login failed"),
        }));
        throw error;
      }
    },
    [refreshOrganizations],
  );

  const loginWithTokens = useCallback(
    async (accessToken: string, refreshToken: string, user: User) => {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

      const result = getLoginWithTokensUseCase().execute(accessToken, refreshToken, user);
      if (!result.success) {
        const errorMsg = result.error || "Token login failed";
        setAuthState((prev) => ({
          ...prev,
          isLoading: false,
          error: errorMsg,
        }));
        throw new Error(errorMsg);
      }

      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      identifyUser(user);
    },
    [],
  );

  const logout = useCallback(async () => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));

    try {
      await getLogoutUseCase().execute();
    } catch {
      // LogoutUseCase already clears storage and Sentry context in its finally block
    } finally {
      resetAnalytics();
      session.clear();
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  }, [session]);

  const updateUser = useCallback((updatedUser: Partial<User>) => {
    let mergedUser: User | null = null;
    setAuthState((prev) => {
      if (!prev.user) return prev;
      mergedUser = { ...prev.user, ...updatedUser };
      return { ...prev, user: mergedUser };
    });
    if (mergedUser) {
      getUserStorageService().setCurrentUser(mergedUser);
    }
  }, []);

  // Handle session expiration events dispatched by the API client interceptor
  useEffect(() => {
    return getSessionExpiredNotifier().subscribe((message?: string) => {
      getLogoutUseCase()
        .execute()
        .catch(() => {
          // Ignore errors — we are already handling the expired session state
        })
        .finally(() => {
          resetAnalytics();
          session.clear();
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });

          showToast(message || "Your session has expired. Please log in again.", "error", 5000);
          getLoginRedirectNotifier().notify();
        });
    });
  }, [session, showToast]);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const value: IAuthContext = {
    user: authState.user,
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    error: authState.error,
    login,
    loginWithTokens,
    logout,
    checkAuthStatus,
    updateUser,
    tokenManagement: getTokenManager(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
